/**
 * Keycap notation for Football 101 (docs/TUTORIAL.md §3.8). Caps always come from the REAL bindings of the device
 * the player uses (`InputManager.promptFor`), never from hard-coded letters, and each way of pressing has its own
 * look so "hold", "twice", "together" and "then" are never confused:
 *
 *   tap        [J]
 *   hold       [J] with a HOLD ribbon (and a fill bar while the key is down)
 *   double     TAP-TAP [D][D] (two caps, one tap each)
 *   chord      [S]+[J]  TOGETHER
 *   then       [W] › [S]  (never the → used for directions)
 *   holdWhile  HOLD [J] › [S]  (keep holding the first, then press the second: "+" read as a chord)
 *   wait       a "hold still" disc, no key (a defender before GO)
 *
 * Text tokens: {A} = a cap, {hold:A}, {2x:R}, {D+A}, {U>D}.
 */
import { Fragment, type ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { ButtonName } from '@hf/shared';
import { DEVICE_ID, padIndexOf, type InputManager } from '@hf/client-core/input/types';
import { useServices } from '../actions';
import type { KeyMode, KeyPrompt, NowPrompt } from '@hf/client-core/ui-model/tutorial/catalog';
import type { FakeInfo } from '@hf/client-core/ui-model/tutorial/fakes';

/**
 * The device prompts should be labelled for: the last device the player touched, if it drives slot 0 (any pad
 * counts when the slot accepts ANY_PAD), else the slot's first device. Re-checked twice a second so the caps follow
 * a player who picks up a pad mid-lesson.
 */
export const useTutorialDevice = (): string => {
  const { input } = useServices();
  const [device, setDevice] = useState(() => pickDevice(input));
  useEffect(() => {
    const t = setInterval(() => {
      const d = pickDevice(input);
      setDevice((cur) => (cur === d ? cur : d));
    }, 500);
    return () => clearInterval(t);
  }, [input]);
  return device;
};

export const pickDevice = (
  input: Pick<InputManager, 'assigned' | 'lastActiveDevice'> & Partial<Pick<InputManager, 'touch'>>,
): string => {
  let assigned: readonly string[] = [];
  try {
    assigned = input.assigned(0);
  } catch {
    assigned = [];
  }
  const last = input.lastActiveDevice?.() ?? null;
  // the on-screen pad is up and nothing else was touched yet: label for the thumbs
  if (
    (last === null || last === DEVICE_ID.TOUCH) &&
    input.touch?.visible &&
    assigned.includes(DEVICE_ID.TOUCH)
  )
    return DEVICE_ID.TOUCH;
  const usable =
    last !== null &&
    (assigned.includes(last) || (padIndexOf(last) >= 0 && assigned.includes(DEVICE_ID.ANY_PAD)));
  return usable ? last : (assigned[0] ?? DEVICE_ID.KB_SOLO);
};

/** One cap for a logical button on the device. */
export function Cap({
  button,
  device,
  size = 'md',
}: {
  button: ButtonName;
  device: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { input } = useServices();
  let label = button as string;
  try {
    label = input.promptFor(device, button);
  } catch {
    /* unknown device: the logical name */
  }
  const pad = padIndexOf(device) >= 0 || device === DEVICE_ID.ANY_PAD;
  const touch = device === DEVICE_ID.TOUCH;
  // a controller direction is a D-PAD with that arm lit ("D←" read as the keyboard's D key)
  const dir = pad ? DPAD_DIR[label] : undefined;
  if (dir) {
    return (
      <span class={`hf-cap ${size} pad dpad`} aria-label={`D-pad ${dir}`}>
        <DpadGlyph dir={dir} />
      </span>
    );
  }
  const long = label.length > 2;
  return (
    <span class={`hf-cap ${size} ${pad ? 'pad' : ''} ${touch ? 'touch' : ''} ${long ? 'long' : ''}`}>
      <span class="hf-cap__face">{label}</span>
    </span>
  );
}

const DPAD_DIR: Record<string, 'left' | 'right' | 'up' | 'down'> = {
  'D←': 'left',
  'D→': 'right',
  'D↑': 'up',
  'D↓': 'down',
};

/** A plus-shaped D-pad with one arm lit. */
export function DpadGlyph({ dir }: { dir: 'left' | 'right' | 'up' | 'down' }) {
  const arm = (d: typeof dir, x: number, y: number, w: number, h: number) => (
    <rect x={x} y={y} width={w} height={h} rx="2.5" class={d === dir ? 'on' : ''} />
  );
  return (
    <svg viewBox="0 0 32 32" class="hf-dpad" aria-hidden="true">
      {arm('up', 11, 2, 10, 11)}
      {arm('down', 11, 19, 10, 11)}
      {arm('left', 2, 11, 11, 10)}
      {arm('right', 19, 11, 11, 10)}
      <rect x="11" y="11" width="10" height="10" />
    </svg>
  );
}

/** Caps for one prompt, in its notation. */
export function Caps({
  buttons,
  mode,
  device,
  size = 'md',
}: {
  buttons: readonly ButtonName[];
  mode: KeyMode;
  device: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const a = buttons[0] ?? 'A';
  const b = buttons[1];
  switch (mode) {
    case 'hold':
      return (
        <span class="hf-caps hold">
          <span class="hf-caps__ribbon t-broadcast">HOLD</span>
          <Cap button={a} device={device} size={size} />
        </span>
      );
    case 'double':
      // two caps, one tap each ("[D][D] ×2" read as four presses — QA round 2); the big caps carry a TAP-TAP
      // ribbon (like HOLD), the small inline ones are just the pair
      return (
        <span class={`hf-caps double ${size}`}>
          {size === 'sm' ? null : <span class="hf-caps__ribbon t-broadcast">TAP-TAP</span>}
          <span class="hf-caps__pair">
            <Cap button={a} device={device} size={size} />
            <Cap button={a} device={device} size={size} />
          </span>
        </span>
      );
    case 'chord':
      return (
        <span class="hf-caps chord">
          <Cap button={a} device={device} size={size} />
          <span class="hf-caps__op">+</span>
          {b ? <Cap button={b} device={device} size={size} /> : null}
        </span>
      );
    case 'then':
      return (
        <span class="hf-caps then">
          <Cap button={a} device={device} size={size} />
          {b && b !== a ? (
            <>
              <span class="hf-caps__then" aria-label="then">
                ›
              </span>
              <Cap button={b} device={device} size={size} />
            </>
          ) : b === a ? (
            <>
              <span class="hf-caps__then" aria-label="then">
                ›
              </span>
              <span class="hf-caps hold inline">
                <span class="hf-caps__ribbon t-broadcast">HOLD</span>
                <Cap button={b} device={device} size={size} />
              </span>
            </>
          ) : null}
        </span>
      );
    case 'holdWhile':
      // "hold J, then tap S" (playtest #4: "HOLD J + S" read as a chord, the line said "hold J, then tap S")
      return (
        <span class="hf-caps hold-while">
          <span class="hf-caps hold inline">
            <span class="hf-caps__ribbon t-broadcast">HOLD</span>
            <Cap button={a} device={device} size={size} />
          </span>
          <span class="hf-caps__then" aria-label="then">
            ›
          </span>
          {b ? <Cap button={b} device={device} size={size} /> : null}
        </span>
      );
    case 'release':
      // the held key, lifted: a LET GO ribbon over the cap (the bubble turns green; red when it is late)
      return (
        <span class="hf-caps release">
          <span class="hf-caps__ribbon t-broadcast">LET GO</span>
          <Cap button={a} device={device} size={size} />
        </span>
      );
    case 'wait':
      return (
        <span class={`hf-caps wait ${size}`} aria-label="wait">
          <span class="hf-wait">
            <i />
            <i />
          </span>
        </span>
      );
    default:
      return <Cap button={a} device={device} size={size} />;
  }
}

/** A coach-panel key: caps + the verb under them. */
export function KeyChip({ k, device }: { k: KeyPrompt; device: string }) {
  return (
    <span class={`hf-keychip mode-${k.mode}`}>
      <Caps buttons={k.buttons} mode={k.mode} device={device} />
      <span class="hf-keychip__verb t-broadcast">{k.verb}</span>
    </span>
  );
}

/** The world bubble's caps. */
export function NowCaps({ now, device }: { now: NowPrompt; device: string }) {
  if (now.tone === 'ready')
    return (
      <span class="hf-caps ready">
        <span class="hf-caps__ribbon t-broadcast">GET READY</span>
        <Cap button={now.button} device={device} size="lg" />
      </span>
    );
  const buttons: ButtonName[] = now.then ? [now.button, now.then] : [now.button];
  return <Caps buttons={buttons} mode={now.mode} device={device} size="lg" />;
}

const TOKEN = /\{(hold:|2x:)?([LRUDAS])(?:([+>])([LRUDAS]))?\}/g;

/** A coach line with {tokens} rendered as inline caps. */
export function RichLine({ text, device }: { text: string; device: string }) {
  const out: ComponentChildren[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) out.push(<Fragment key={`t${i++}`}>{text.slice(last, at)}</Fragment>);
    const kind = m[1];
    const a = m[2] as ButtonName;
    const op = m[3];
    const b = m[4] as ButtonName | undefined;
    // inline, the sentence already says "hold": the cap alone reads better than a second HOLD ribbon
    const mode: KeyMode = op === '+' ? 'chord' : op === '>' ? 'then' : kind === '2x:' ? 'double' : 'tap';
    out.push(
      <span class="hf-inlinecap" key={`c${i++}`}>
        <Caps buttons={b ? [a, b] : [a]} mode={mode} device={device} size="sm" />
      </span>,
    );
    last = at + m[0].length;
  }
  if (last < text.length) out.push(<Fragment key={`t${i++}`}>{text.slice(last)}</Fragment>);
  return <>{out}</>;
}

/** A fake's inputs (fakes.ts): the lead-in (dash / let go / in the air) and its caps. */
export function FakeKeys({ f, device, size = 'sm' }: { f: FakeInfo; device: string; size?: 'sm' | 'md' }) {
  return (
    <span class="hf-fakekeys">
      {f.lead === 'dash' ? (
        <>
          <Caps buttons={['R']} mode="double" device={device} size={size} />
          <span class="hf-caps__then">›</span>
        </>
      ) : f.lead ? (
        <>
          <span class="hf-fake__word t-broadcast">{f.lead === 'stop' ? 'LET GO' : 'IN THE AIR'}</span>
          <span class="hf-caps__then">›</span>
        </>
      ) : null}
      <Caps buttons={f.keys} mode={f.mode} device={device} size={size} />
    </span>
  );
}
